import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from '../environments/environment';
import { AuthService } from './services/auth.service';
import { LocalStorageService } from './services/local-storage.service';
import { showConfirmAlert } from './utils/alert';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
	title = 'frontend-sigp';

	private authSubscription?: Subscription;
	private sessionCounterInterval: any;
	private activityDebounceTimeout: any;
	private activityDebounceDelay: number = 5000;
	private activityHandler = () => this.updateLastActivity();
	private inactivityInMinutes: number = environment.inactivityInMinutes;
	private globalSessionTimeout: number = environment.globalSessionTimeout;

	constructor(
		private authService: AuthService,
		private ls: LocalStorageService,
	) { }

	ngOnInit(): void {
		this.authSubscription = this.authService.authStatus$.subscribe((isAuthenticated) => {
			if (isAuthenticated) {
				this.initializeSessionMonitoring();
			} else {
				this.cleanupSessionMonitoring();
			}
		});
	}

	ngOnDestroy(): void {
		if (this.authSubscription) {
			this.authSubscription.unsubscribe();
		}
		this.cleanupSessionMonitoring();
	}

	private initializeSessionMonitoring(): void {
		this.attachActivityListeners();
		this.startSessionCounter();
		this.startGlobalSessionTimeout();
		this.startRefreshTokenInterval();
		localStorage.setItem('lastActivity', new Date().getTime().toString());
	}

	private cleanupSessionMonitoring(): void {
		this.removeActivityListeners();
		if (this.activityDebounceTimeout) {
			clearTimeout(this.activityDebounceTimeout);
		}
		if (this.sessionCounterInterval) {
			clearInterval(this.sessionCounterInterval);
		}
	}

	private updateLastActivity(): void {
		if (this.activityDebounceTimeout) {
			clearTimeout(this.activityDebounceTimeout);
		}

		this.activityDebounceTimeout = setTimeout(() => {
			localStorage.setItem('lastActivity', new Date().getTime().toString());
		}, this.activityDebounceDelay);
	}

	private attachActivityListeners(): void {
		const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
		events.forEach((event) => {
			document.addEventListener(event, this.activityHandler, true);
		});
	}

	private removeActivityListeners(): void {
		const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
		events.forEach((event) => {
			document.removeEventListener(event, this.activityHandler, true);
		});
	}

	private startSessionCounter(): void {
		if(!environment.production){
			return;
		}

		this.sessionCounterInterval = setInterval(() => {
			const lastActivity = Number(localStorage.getItem('lastActivity'));
			const currentTime = new Date().getTime();
			const inactivityTime = (currentTime - lastActivity) / 1000 / 60; // Convert to minutes

			if (inactivityTime >= this.inactivityInMinutes) {
				this.handleSessionExpired(this.inactivityInMinutes);
			}
		}, 60000); // Check inactivity every minute
	}

	private handleSessionExpired(minutes: number): void {
		this.cleanupSessionMonitoring();
		this.closeSession();

		showConfirmAlert({
			message: `Su sesión expiró tras ${minutes} minutos de inactividad. Inicie sesión nuevamente para acceder al sistema.`,
			type: 'info',
			msgConfirm: 'Aceptar',
			showCancelButton: false,
			dismissible: false,
		});
	}

	// Global session timeout handling, even if the user is active, the session will expire after a certain time
	private startGlobalSessionTimeout(): void {
		if(!environment.production){
			return;
		}
		const globalSessionTimeout = environment.globalSessionTimeout * 60 * 1000;

		setTimeout(() => {
			this.handleSessionExpired(this.globalSessionTimeout);
		}, globalSessionTimeout);
	}

	private closeSession(): void {
		this.authService.logout().subscribe({
			next: () => {
				window.location.href = '/auth/login';
			},
			error: () => {
				window.location.href = '/auth/login';
			}
		});
	}

	private startRefreshTokenInterval(): void {
		const expiresInDateStr = this.ls.getItem('expiresInDate');
		const expiresInDate = new Date(expiresInDateStr);

		const timeoutDuration = expiresInDate.getTime() - new Date().getTime() - 60000; // Refresh 1 minute before expiration

		setTimeout(() => {
			this.authService.refreshToken().subscribe(() => {
				this.startRefreshTokenInterval(); // Restart the interval after refreshing the token
			});
		}, timeoutDuration); // Refresh 1 minute before expiration
	}
}
